import React, { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ClipboardList, Tag, Ticket, MapPin, Settings as SettingsIcon,
  Users, LogOut, Loader2, Star, History, MailQuestion, Building2, Handshake, UserCog, ClipboardCheck, ShieldCheck, Bell, QrCode, Image as ImageIcon,
} from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api, unwrap } from "@/lib/api";
import SEO from "@/components/shared/SEO";

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingApps, setPendingApps] = useState(0);
  const [pendingIdentity, setPendingIdentity] = useState(0);
  const [newWaitlist, setNewWaitlist] = useState(0);
  const [cleanerResponses, setCleanerResponses] = useState(0);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/cleaner-applications/pending-count")
      .then((r) => setPendingApps(unwrap(r)?.count || 0))
      .catch(() => {});
    api.get("/admin/identity-checks?status=pending_review")
      .then((r) => setPendingIdentity((unwrap(r)?.items || []).length))
      .catch(() => {});
    api.get("/admin/waitlist/new-count")
      .then((r) => setNewWaitlist(unwrap(r)?.count || 0))
      .catch(() => {});
  }, [user]);

  // Cleaner responses badge — refetch on every navigation so it clears right
  // after the admin visits /admin/cleaner-responses (which marks them seen).
  useEffect(() => {
    if (!user) return;
    api.get("/admin/cleaner-responses/unseen-count")
      .then((r) => setCleanerResponses(unwrap(r)?.count || 0))
      .catch(() => {});
  }, [user, location.pathname]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const items = [
    { to: "/admin", icon: LayoutDashboard, label: t("admin.dashboard"), end: true },
    { to: "/admin/bookings", icon: ClipboardList, label: t("admin.bookings") },
    { to: "/admin/coordination", icon: Handshake, label: t("admin.coordination") },
    { to: "/admin/pricing", icon: Tag, label: t("admin.pricing") },
    { to: "/admin/promos", icon: Ticket, label: t("admin.promos") },
    { to: "/admin/reviews", icon: Star, label: t("admin.reviews") },
    { to: "/admin/waitlist", icon: MailQuestion, label: t("admin.waitlist"), badge: newWaitlist },
    { to: "/admin/cleaner-responses", icon: Bell, label: "Cleaner responses", badge: cleanerResponses },
    { to: "/admin/hosts", icon: Building2, label: t("admin.hosts") },
    { to: "/admin/zones", icon: MapPin, label: t("admin.zones") },
    { to: "/admin/settings", icon: SettingsIcon, label: t("admin.settings") },
    { to: "/admin/users", icon: Users, label: t("admin.users") },
    { to: "/admin/cleaners", icon: UserCog, label: t("admin.cleaners") },
    { to: "/admin/applications", icon: ClipboardCheck, label: t("admin.applications"), badge: pendingApps },
    { to: "/admin/identity-checks", icon: ShieldCheck, label: t("admin.identity_checks"), badge: pendingIdentity },
    { to: "/admin/audit", icon: History, label: t("admin.audit_log") },
  ];

  const toolsItems = [
    { to: "/admin/qr-generator", icon: QrCode, label: "QR Generator" },
    { to: "/admin/gallery", icon: ImageIcon, label: "Marketing Gallery" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="admin-layout">
      <SEO title="Admin — Ever Clean Home" description="Ever Clean Home administration panel." noindex />
      <div className="lg:block">
        {/* Sidebar */}
        <aside className="bg-white border-r border-[#E5E7EB] lg:min-h-screen p-4 lg:p-5 flex lg:flex-col lg:fixed lg:top-0 lg:left-0 lg:w-[260px] lg:h-screen overflow-y-auto">
          <div className="flex items-center gap-2 px-2 py-3">
            <img src="/brand/logo-everclean-icon-192.png" alt="Ever Clean Home" className="w-12 h-12 object-contain" />
            <div>
              <div className="font-display font-extrabold text-sm">Ever Clean</div>
              <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">Admin</div>
            </div>
          </div>
          <nav className="mt-4 flex flex-row lg:flex-col gap-1 overflow-x-auto">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive ? "bg-[#0EA5A4]/10 text-[#0F766E]" : "text-[#0C1A1A] hover:bg-[#F5F5F4]"
                  }`}
                data-testid={`admin-nav-${it.to.split("/").pop() || "dashboard"}`}>
                <it.icon className="w-4 h-4" />
                <span>{it.label}</span>
                {it.badge > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#0EA5A4] text-white text-[11px] font-bold grid place-items-center" data-testid={`admin-nav-badge-${it.to.split("/").pop() || "dashboard"}`}>
                    {it.badge}
                  </span>
                )}
              </NavLink>
            ))}
            <div className="lg:mt-3 lg:pt-3 lg:border-t border-[#E5E7EB] flex lg:flex-col gap-1 lg:w-full">
              <span className="hidden lg:block px-3 pb-1 text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Tools</span>
              {toolsItems.map((it) => (
                <NavLink key={it.to} to={it.to}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive ? "bg-[#0EA5A4]/10 text-[#0F766E]" : "text-[#0C1A1A] hover:bg-[#F5F5F4]"
                    }`}
                  data-testid={`admin-nav-${it.to.split("/").pop() || "dashboard"}`}>
                  <it.icon className="w-4 h-4" />
                  <span>{it.label}</span>
                </NavLink>
              ))}
            </div>
          </nav>
          <div className="lg:mt-auto pt-4 hidden lg:block">
            <div className="text-xs text-[#4B5563] px-2 mb-2">Signed in as</div>
            <div className="text-sm font-semibold px-2 truncate">{user.email}</div>
            <button onClick={() => { logout(); navigate("/admin/login"); }}
              className="mt-3 flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-[#4B5563] hover:bg-[#F5F5F4]"
              data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> {t("admin.logout")}
            </button>
          </div>
        </aside>

        <main className="lg:ml-[260px] p-4 md:p-8" data-testid="admin-main">
          <div className="max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
